import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@/components/base/Button';
import StatCard from '@/components/base/StatCard';
import FilterSelect from '@/components/base/FilterSelect';
import MarketSelect from '@/components/base/MarketSelect';
import ExportMenu from '@/components/feature/ExportMenu';
import { buildCsv } from '@/utils/export';
import { useRole } from '@/context/RoleContext';
import { api, apiList } from '@/utils/api';
import { formatMoney } from '@/utils/format';
import { toKesEquivalent } from '@/utils/invoice';
import { payoutCounts } from '@/utils/payout';
import type { Payout, RailStatus } from '@/types/portal';
import RailStatusStrip from './components/RailStatusStrip';
import PayoutTable from './components/PayoutTable';
import PayoutExceptionsPanel from './components/PayoutExceptionsPanel';
import RetryPayoutModal from './components/RetryPayoutModal';
import PayoutUploadModal from './components/PayoutUploadModal';

const PAYOUT_TABS = [
  { id: 'all', label: 'All', icon: 'ri-list-check' },
  { id: 'Scheduled', label: 'Scheduled', icon: 'ri-calendar-line' },
  { id: 'Processing', label: 'Processing', icon: 'ri-loader-4-line' },
  { id: 'Completed', label: 'Completed', icon: 'ri-checkbox-circle-line' },
  { id: 'Failed', label: 'Failed', icon: 'ri-error-warning-line' },
];

const messageOf = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

export default function Payouts() {
  const navigate = useNavigate();
  const { can } = useRole();
  const canManage = can('payouts.manage');

  const [status, setStatus] = useState('all');
  // The "payout failed" alert opens ?status=Failed (also when already here).
  const statusParam = useSearchParams()[0].get('status');
  useEffect(() => {
    if (statusParam) setStatus(statusParam);
  }, [statusParam]);
  const [markets, setMarkets] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [retryTarget, setRetryTarget] = useState<Payout | null>(null);
  const [retried, setRetried] = useState(false);
  const [notice, setNotice] = useState('');
  const [allPayouts, setAllPayouts] = useState<Payout[]>([]);
  const [rails, setRails] = useState<RailStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    apiList<Payout>('/payouts')
      .then(({ data }) => setAllPayouts(data))
      .catch((loadError: unknown) => setError(messageOf(loadError, 'Could not load the payouts.')))
      .finally(() => setLoading(false));
    api<RailStatus[]>('/rails/status')
      .then((data) => setRails(data ?? []))
      .catch(() => setRails([]));
  }, [reloadKey]);

  const statusOf = (payout: Payout) => overrides[payout.id] ?? payout.status;

  const counts = useMemo(() => {
    const result: Record<string, number> = { all: allPayouts.length };
    allPayouts.forEach((payout) => {
      const value = overrides[payout.id] ?? payout.status;
      result[value] = (result[value] ?? 0) + 1;
    });
    return result;
  }, [allPayouts, overrides]);

  const tabs = PAYOUT_TABS.map((tab) => ({
    ...tab,
    count: tab.id === 'all' ? allPayouts.length : counts[tab.id] ?? 0,
  }));

  const filtered = useMemo(() => {
    return allPayouts.filter((payout) => {
      const value = overrides[payout.id] ?? payout.status;
      if (status !== 'all' && value !== status) return false;
      if (markets.length > 0 && !markets.includes(payout.market)) return false;
      if (query.trim()) {
        const text = query.toLowerCase();
        if (
          !payout.creatorName.toLowerCase().includes(text) &&
          !payout.reference.toLowerCase().includes(text) &&
          !payout.campaign.toLowerCase().includes(text)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [allPayouts, status, markets, query, overrides]);

  const baseCounts = payoutCounts(allPayouts);
  const inFlightKes = allPayouts
    .filter((payout) => ['Scheduled', 'Processing'].includes(statusOf(payout)))
    .reduce((sum, payout) => sum + toKesEquivalent(payout.amount, payout.currency), 0);

  const openRetry = (payout: Payout) => {
    setRetried(false);
    setRetryTarget(payout);
  };

  const confirmRetry = async () => {
    if (!retryTarget) return;
    setError('');
    try {
      const updated = await api<Payout>(`/payouts/${retryTarget.id}/retry`, {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      });
      setAllPayouts((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      setOverrides((prev) => ({ ...prev, [retryTarget.id]: updated.status }));
      setNotice(`Retry queued for ${retryTarget.reference} · ${retryTarget.creatorName}.`);
      setRetried(true);
    } catch (retryError) {
      setRetryTarget(null);
      setError(messageOf(retryError, 'Could not retry the payout.'));
    }
  };

  const closeRetry = () => {
    setRetryTarget(null);
    setRetried(false);
  };

  const buildPayoutCsv = () =>
    buildCsv(
      [
        'Reference',
        'Creator',
        'Campaign',
        'Market',
        'Rail',
        'Currency',
        'Amount',
        'Status',
        'Scheduled',
        'Completed',
        'Exception',
        'Attempts',
      ],
      filtered.map((payout) => [
        payout.reference,
        payout.creatorName,
        payout.campaign,
        payout.market,
        payout.rail,
        payout.currency,
        payout.amount,
        statusOf(payout),
        payout.scheduledAt,
        payout.completedAt ?? '',
        payout.exception ?? '',
        payout.attempts,
      ]),
    );

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Payouts
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Disbursements in flight and settled, with rail health and exceptions.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportMenu
            filename="twiva-payouts"
            pdfTitle="Payouts"
            label="Export payouts"
            buildCsv={buildPayoutCsv}
            onExported={(format) =>
              setNotice(
                format === 'csv'
                  ? `CSV exported — ${filtered.length} payout${filtered.length === 1 ? '' : 's'}.`
                  : 'PDF ready — choose "Save as PDF" in the print dialog.',
              )
            }
          />
          <Button variant="outline" icon="ri-upload-cloud-2-line" disabled={!canManage} onClick={() => setUploadOpen(true)}>
            Upload payouts
          </Button>
          <Button
            icon="ri-send-plane-line"
            disabled={!canManage}
            onClick={() => navigate('/payments/send')}
          >
            Pay creator
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Scheduled"
          value={String(baseCounts.scheduled)}
          icon="ri-calendar-line"
          tone="secondary"
          hint="Queued for the next settlement window"
        />
        <StatCard
          label="Processing"
          value={String(baseCounts.processing)}
          icon="ri-loader-4-line"
          tone="primary"
          hint="Currently on a payout rail"
        />
        <StatCard
          label="Completed"
          value={String(baseCounts.completed)}
          icon="ri-checkbox-circle-line"
          tone="accent"
          hint="Settled and reconciled"
        />
        <StatCard
          label="Failed / held"
          value={String(baseCounts.failed + baseCounts.onHold)}
          icon="ri-error-warning-line"
          tone="neutral"
          hint={`${baseCounts.failed} failed · ${baseCounts.onHold} on hold`}
        />
      </div>

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="mt-5 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5"
        >
          <span className="inline-flex items-center gap-2 text-sm text-primary-800">
            <i className="ri-error-warning-line" />
            {error}
          </span>
          <button
            type="button"
            onClick={() => setError('')}
            className="cursor-pointer text-primary-700 hover:text-primary-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {!canManage ? (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
          <i className="ri-lock-line" />
          Your current role can view payouts but not manage them. Switch to Finance or Administrator
          to retry or release disbursements.
        </div>
      ) : null}

      <div className="mt-6">
        <RailStatusStrip rails={rails} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 xl:flex-row xl:items-center xl:justify-between">
            <FilterSelect
              value={status}
              onChange={setStatus}
              label="Status"
              className="w-full sm:w-56"
              options={tabs}
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <MarketSelect
                value={markets}
                onChange={setMarkets}
                placeholder="All markets"
                compact
                className="sm:w-56"
              />
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
                <i className="ri-search-line text-foreground-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search creator, ref or campaign..."
                  className="h-9 w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none sm:w-60"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-1 text-xs text-foreground-500">
            <span>
              Showing {filtered.length} of {allPayouts.length} payouts
            </span>
            <span>In flight value · {formatMoney(inFlightKes, 'KES')} KES equiv</span>
          </div>

          {loading ? (
            <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-12 text-center text-sm text-foreground-500">
              Loading payouts…
            </div>
          ) : (
            <PayoutTable rows={filtered} canManage={canManage} onRetry={openRetry} />
          )}
        </div>

        <div>
          <PayoutExceptionsPanel rows={allPayouts} canManage={canManage} onRetry={openRetry} />
        </div>
      </div>

      <PayoutUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmitted={(message) => {
          setUploadOpen(false);
          setNotice(message);
          setReloadKey((key) => key + 1);
        }}
      />

      <RetryPayoutModal
        payout={retryTarget}
        onClose={closeRetry}
        onConfirm={() => void confirmRetry()}
        retried={retried}
      />
    </div>
  );
}